import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, CheckCircle, XCircle, Clapperboard } from 'lucide-react';

/**
 * ComponentUpdateIndicator - Shows toast notifications when components are being re-rendered
 *
 * @param {Object} props
 * @param {Array} props.updateItems - Array of components currently being updated
 *   Each item: { id, name, type, status: 'loading' | 'success' | 'error', progress, jobStatus }
 */
const ComponentUpdateIndicator = ({ updateItems = [] }) => {
  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'loading':
        return <Loader2 className="w-4 h-4 animate-spin text-ag-green" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-ag-green" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-400" />;
      default:
        return <Loader2 className="w-4 h-4 animate-spin text-ag-green" />;
    }
  };

  // Get status text based on job status
  const getStatusText = (item) => {
    if (item.status === 'success') {
      return 'Updated';
    }
    if (item.status === 'error') {
      return 'Update failed';
    }

    // For loading status, show specific message based on jobStatus
    switch (item.jobStatus) {
      case 'waiting':
        return 'Waking up...';
      case 'queued':
        return 'Queued...';
      case 'started':
      case 'render:dorender':
        return `Rendering ${item.progress ? Math.round(item.progress) + '%' : ''}`.trim();
      default:
        return 'Processing...';
    }
  };

  // Determine progress bar color
  const getProgressBarColor = (item) => {
    if (item.status === 'success') return 'bg-ag-green';
    if (item.status === 'error') return 'bg-red-400';
    return 'bg-ag-green';
  };

  return (
    <div className="fixed bottom-8 right-4 z-50 flex flex-col gap-2 pointer-events-none">
      <AnimatePresence mode="popLayout">
        {updateItems.map((item) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
            transition={{
              duration: 0.2,
              ease: "easeInOut"
            }}
            className="flex flex-col gap-2 px-4 py-3 rounded-lg bg-ag-black-800/95 border border-ag-black-400/50 pointer-events-auto shadow-lg min-w-[250px]"
          >
            {/* Top row: Icon, Name, Status Icon */}
            <div className="flex items-center gap-3">
              {/* Component type icon */}
              <div className="flex-shrink-0 text-ag-green">
                <Clapperboard className="w-4 h-4" />
              </div>

              {/* Content */}
              <div className="flex flex-col min-w-0 flex-1">
                <span className="text-sm text-gray-300 truncate font-medium">
                  {item.name}
                </span>
                <span className="text-xs text-gray-400">
                  {getStatusText(item)}
                </span>
              </div>

              {/* Status icon */}
              <div className="flex-shrink-0">
                {getStatusIcon(item.status)}
              </div>
            </div>

            {/* Progress bar - only show for loading status with actual progress */}
            {item.status === 'loading' && item.progress > 0 && (
              <div className="w-full h-1 bg-ag-black-700 rounded-full overflow-hidden">
                <motion.div
                  className={`h-full ${getProgressBarColor(item)}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${item.progress}%` }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                />
              </div>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default ComponentUpdateIndicator;